import type { FUSportsLivescoreScore } from "fansunited-data-layer";

/** Lowercase sport identifiers for the multi-sport event page. */
export type SportEventSport = "basketball" | "tennis" | "hockey" | "american-football";

/** Human-friendly label used for the score-breakdown table header. */
export function getPeriodLabelKey(sport: SportEventSport): "quarter" | "set" | "period" {
    switch (sport) {
        case "basketball":
        case "american-football":
            return "quarter";
        case "hockey":
            return "period";
        case "tennis":
            return "set";
    }
}

const AGGREGATE_SCORE_KEYS = new Set(["result", "full_time", "half_time"]);

export type PeriodKind = "regulation" | "overtime" | "penalty";

export interface PeriodScoreEntry {
    key: string;
    /** Short label rendered in the column header (e.g. "Q1", "OT", "П"). */
    label: string;
    /** Kind of period — used to look up the long tooltip label. */
    kind: PeriodKind;
    /** Sequential number for the kind (1 for the first regulation period, first OT, etc.). */
    number: number;
    one: string;
    two: string;
}

/**
 * Extract per-period scores from the flat score array. Preserves API ordering
 * and skips the aggregate lines (result/full_time/half_time). OT and penalty
 * keys get distinct "OT" / "P" labels instead of being counted as another
 * regulation period. Tennis tiebreak sub-scores are folded into their parent
 * set (the API emits `set2` alongside `set2_tiebreak`).
 */
export function extractPeriodScores(
    scores: FUSportsLivescoreScore[] | undefined,
    sport: SportEventSport,
    periodLabelPrefix: string
): PeriodScoreEntry[] {
    if (!scores?.length) return [];
    // Sort regulation first, then OT, then penalty — the API ships entries in
    // arbitrary order (hockey emits `extra_time` and `penalties` *before*
    // `p1`-`p3`), so without this the detail page would render OT before P1.
    // Within each kind, sort by the numeric suffix of the key (`p2` → 2,
    // `ot1` → 1) so columns are chronological.
    const KIND_ORDER = { regulation: 0, overtime: 1, penalty: 2 } as const;
    const sorted = scores
        .map((s, insertionIndex) => {
            const kind = classifyPeriodKey(s.key);
            const numMatch = s.key.match(/(\d+)/);
            const sortIndex = numMatch ? Number(numMatch[1]) : Number.MAX_SAFE_INTEGER;
            return { s, kind, sortIndex, insertionIndex };
        })
        .sort((a, b) => {
            if (KIND_ORDER[a.kind] !== KIND_ORDER[b.kind]) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
            return a.sortIndex - b.sortIndex || a.insertionIndex - b.insertionIndex;
        });

    const out: PeriodScoreEntry[] = [];
    let index = 0;
    let otIndex = 0;
    let penaltyIndex = 0;
    // Dedup for OT/penalty only: AF emits both `ot1` and `extra_time` for the
    // same OT period with identical scores — one would render as a phantom
    // extra column. Regulation periods come as distinct keys (`p1`/`p2`/`p3`,
    // `q1`-`q4`) and legitimately repeat scores — hockey can easily have
    // P1=0-1 and P2=0-1, which must NOT dedup.
    const seenPostRegulation = new Set<string>();
    for (const { s, kind } of sorted) {
        if (AGGREGATE_SCORE_KEYS.has(s.key)) continue;
        if (sport === "tennis" && s.key.endsWith("_tiebreak")) continue;
        // Hockey/AF feeds ship `ot1`..`ot5` (and `extra_time`, `penalties`) as
        // empty-string placeholders even when none were played. Drop them so
        // we don't render phantom OT/penalty columns on regulation-only games.
        if (s.competitorOne === "" && s.competitorTwo === "") continue;
        let label: string;
        let number: number;
        if (kind === "overtime") {
            const signature = `overtime:${s.competitorOne}:${s.competitorTwo}`;
            if (seenPostRegulation.has(signature)) continue;
            seenPostRegulation.add(signature);
            otIndex += 1;
            number = otIndex;
            label = otIndex === 1 ? "OT" : `OT${otIndex}`;
        } else if (kind === "penalty") {
            const signature = `penalty:${s.competitorOne}:${s.competitorTwo}`;
            if (seenPostRegulation.has(signature)) continue;
            seenPostRegulation.add(signature);
            penaltyIndex += 1;
            number = penaltyIndex;
            label = "P";
        } else {
            // Regulation periods use just the number — the row-level heading
            // (Quarter / Set / Third / Period) already carries the unit, so
            // "1 2 3 4" is clearer than "Q1 Q2 Q3 Q4".
            index += 1;
            number = index;
            label = String(index);
        }
        out.push({ key: s.key, label, kind, number, one: s.competitorOne, two: s.competitorTwo });
    }
    return out;
}

function classifyPeriodKey(key: string): PeriodKind {
    const k = key.toLowerCase();
    if (k.includes("penalt") || k === "so" || k.startsWith("shootout")) return "penalty";
    // American football (and some hockey feeds) emit `extra_time` alongside
    // `ot1` — both mean overtime. Match anything containing "overtime" or
    // "extra_time", plus the shorter aliases (`ot`, `et`).
    if (k.includes("overtime") || k.includes("extra_time") || k.startsWith("ot") || k === "et") return "overtime";
    return "regulation";
}

/** Full-time / result score used for the header. */
export function getAggregateScore(
    scores: FUSportsLivescoreScore[] | undefined
): { one: string; two: string } | null {
    if (!scores?.length) return null;
    const line =
        scores.find((s) => s.key === "result") ??
        scores.find((s) => s.key === "full_time") ??
        scores[0];
    return line ? { one: line.competitorOne, two: line.competitorTwo } : null;
}

/**
 * Post-regulation resolution info. Returned only when the winner was decided
 * after regular time — `null` for regulation wins and unfinished matches.
 *
 * `code` maps to labels.matchPage.status: "aet" for overtime / extra time,
 * "ap" for penalty shootouts. `code === null` covers "REGULAR_TIME" and
 * any reason we don't recognise (safer to omit than mislabel).
 */
export type PostRegulationCode = "aet" | "ap";

export function resolvePostRegulationCode(reason: string | undefined | null): PostRegulationCode | null {
    if (!reason) return null;
    const upper = reason.toUpperCase();
    if (upper.includes("PENALT")) return "ap"; // PENALTIES, PENALTY_SHOOTOUT
    if (upper.includes("OVERTIME") || upper.includes("EXTRA_TIME") || upper.includes("EXTRATIME")) return "aet";
    if (upper.includes("SHOOTOUT")) return "ap"; // hockey shootout
    return null;
}

/**
 * Stat keys that should never render for a given sport because they carry
 * no signal there. Example: `no_draw` (streak without a draw) is meaningless
 * for tennis / basketball / American football — those sports don't draw, so
 * the value is always equal to `total_matches_played` and misleads readers
 * into thinking it's a metric worth tracking.
 *
 * The check is purely a "hide this row" filter — the API still returns the
 * data, we just don't show it. Keep in sync with `SPORTS_WITHOUT_DRAWS` in
 * `H2HPanel.tsx`.
 */
/**
 * Per-sport curated whitelists for the Stats tab.
 *
 * The Sports API returns a firehose of stats for every event — including
 * plenty that are structurally nonsensical for the sport being rendered
 * (e.g. `goals_conceded_per_match` for tennis, `over_0_5_ft` returning
 * 100% for basketball on every event, `scored_in_both_halves` for hockey
 * where the concept doesn't exist). Rather than blacklist noise ad-hoc,
 * this file locks down which keys are *allowed* per sport, in the order
 * we want to render them.
 *
 * Sample events used to design these lists:
 *   tennis          tn:m:13749  (finished)
 *   american-fb     af:m:632    (finished, 3-game season sample)
 *   hockey          hk:m:5884   (scheduled — in-match stats undetermined)
 *   basketball      bb:m:26303  (finished, no in-match stats returned by API)
 *
 * Any key not in the whitelist is dropped. Unknown-but-genuine stats that
 * emerge later can be added here as we discover them — a whitelist is the
 * only design that stays honest when the underlying API keeps growing.
 */
export interface SportStatWhitelist {
    /** Keys to render in the "Match stats" section (event.competitor_*.stats). */
    matchStats: string[];
    /** Keys to render in the "Recent form indicators" section (data.stats.competitor_*). */
    aggregateStats: string[];
}

export const SPORT_STAT_WHITELIST: Record<SportEventSport, SportStatWhitelist> = {
    tennis: {
        matchStats: [
            "points",
            "aces",
            "double_faults",
            "service_faults",
            "first_serve_pct",
            "service_points_won",
            "return_points_won",
            "total_points_won",
            "break_points_won",
            "break_points",
            "break_points_won_percentage",
            "break_points_saved",
            "service_games",
            "1st_services_in",
            "1st_services_attempts",
            "2nd_services_attempts",
            "1st_return_points_won",
            "2nd_return_points_won",
            "first_service_points",
            "second_service_points",
        ],
        aggregateStats: [
            "matches_played",
            "consecutive_wins",
            "consecutive_losses",
            "consecutive_unbeaten",
            "no_win",
            "no_defeat",
        ],
    },
    basketball: {
        matchStats: [
            "points",
            "fg_scored",
            "fg_attempts",
            "fg_pct",
            "2pt_scored",
            "2pt_attempts",
            "3pt_scored",
            "3pt_attempts",
            "3pt_pct",
            "ft_scored",
            "ft_attempts",
            "ft_pct",
            "rebounds",
            "assists",
            "steals",
            "blocks",
            "turnovers",
            "fouls",
        ],
        aggregateStats: [
            "matches_played",
            "consecutive_wins",
            "consecutive_losses",
            "consecutive_unbeaten",
            "no_win",
            "no_defeat",
            "goals_scored",
            "goals_conceded",
            "goals_scored_per_match",
            "goals_conceded_per_match",
            "leading_at_half_time",
            "losing_at_half_time",
        ],
    },
    hockey: {
        matchStats: [
            "goals",
            "assists",
            "shots",
            "shots_on_goal",
            "shots_on_target",
            "powerplay_goals",
            "penalty_minutes",
            "penalties",
            "faceoffs_won",
        ],
        aggregateStats: [
            "matches_played",
            "consecutive_wins",
            "consecutive_draws",
            "consecutive_losses",
            "consecutive_unbeaten",
            "no_win",
            "no_defeat",
            "no_draw",
            "goals_scored",
            "goals_conceded",
            "goals_scored_per_match",
            "goals_conceded_per_match",
            "gs_gc_per_match",
            "clean_sheets",
            "scoring_rate",
        ],
    },
    "american-football": {
        matchStats: [
            "touchdowns",
            "passing_touchdowns",
            "rushing_touchdowns",
            "receiving_touchdowns",
            "field_goals",
            "extra_points",
            "safeties",
            "passes_completed",
            "passing_attempts",
            "extra_point_attempts",
            "field_goal_attempts",
            "extra_point_pct",
            "field_goal_pct",
            "passing_touchdowns_allowed",
            "sacks",
            "fumbles",
            "fumbles_lost",
            "fumbles_recovered",
            "interceptions",
            "interceptions_thrown",
            "turnovers",
            "punts",
            "kickoffs",
            "touchbacks",
            "penalties",
        ],
        aggregateStats: [
            "matches_played",
            "consecutive_wins",
            "consecutive_losses",
            "consecutive_unbeaten",
            "no_win",
            "no_defeat",
            "goals_scored",
            "goals_conceded",
            "goals_scored_per_match",
            "goals_conceded_per_match",
            "leading_at_half_time",
            "losing_at_half_time",
        ],
    },
};

export function whitelistMatchStats(sport: SportEventSport): string[] {
    return SPORT_STAT_WHITELIST[sport].matchStats;
}

export function whitelistAggregateStats(sport: SportEventSport): string[] {
    return SPORT_STAT_WHITELIST[sport].aggregateStats;
}
